// Seller Settings → ShipStation — the seller's own ShipStation API key.
//
// Etsy hides buyer addresses from PEXIFY; the seller's ShipStation account has
// them. Write-only: the key is verified against ShipStation on save, stored
// encrypted and only its last four characters ever come back
// (D-14 LIGHT 2026-09-15, GET/PUT/DELETE /api/v1/seller/shipstation-credential).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, KeyRound, Lock, Truck } from 'lucide-react'
import { useT } from '../../i18n'
import { shipStationCredentialI18n } from '../../i18n/settings/shipstation-credential'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDateTime } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useMe } from '../../lib/useMe'
import type { SettingsSurface } from './Settings'

export type ShipStationCredentialStatus = {
  connected: boolean
  api_key_hint: string | null
  etsy_store_count: number | null
  verified_at: string | null
  last_error: string | null
  last_fill_at: string | null
  last_fill_count: number | null
}

const PATH = '/seller/shipstation-credential'
const QUERY_KEY = ['seller-shipstation-credential'] as const
const INPUT_CLASS =
  'w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[13px] font-mono text-slate-900 outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]'

function detailMessage(body: unknown): string | null {
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const message = (detail as { message: unknown }).message
      if (typeof message === 'string') return message
    }
  }
  return null
}

async function request(init?: RequestInit): Promise<ShipStationCredentialStatus> {
  const response = await fetch(apiUrl(PATH), init)
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(detailMessage(body) ?? `request failed (${response.status})`)
  }
  return (await response.json()) as ShipStationCredentialStatus
}

export function ShipStationCredential({ surface }: { surface: SettingsSurface }) {
  void surface // sellers-only filter at Settings shell level
  const t = useT(shipStationCredentialI18n)
  const queryClient = useQueryClient()
  const { hasCapability } = useMe()
  const canWrite = hasCapability('org_shipstation_credential.write')
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: () => request(), enabled: USE_REAL_API })
  const [editing, setEditing] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')

  const status = query.data
  const connected = Boolean(status?.connected)
  const showForm = canWrite && (!connected || editing)

  const resetForm = () => {
    setApiKey('')
    setApiSecret('')
    setEditing(false)
  }

  const save = useMutation({
    mutationFn: () =>
      request({
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey.trim(), api_secret: apiSecret.trim() }),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
      resetForm()
      toast.success(t.toast.saved, {
        description: t.toast.savedDescription(next.etsy_store_count ?? 0),
        audience: 'seller',
      })
    },
    onError: (error: unknown) => {
      toast.error(t.toast.saveFailed, {
        description: error instanceof Error ? error.message : undefined,
        audience: 'seller',
      })
    },
  })

  const remove = useMutation({
    mutationFn: () => request({ method: 'DELETE' }),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next)
      resetForm()
      toast.success(t.toast.removed, { audience: 'seller' })
    },
    onError: (error: unknown) => {
      toast.error(t.toast.removeFailed, {
        description: error instanceof Error ? error.message : undefined,
        audience: 'seller',
      })
    },
  })

  const canSave =
    USE_REAL_API && apiKey.trim().length >= 8 && apiSecret.trim().length >= 8 && !save.isPending

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-slate-900">{t.pageTitle}</h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">{t.pageDescription}</p>
      </header>

      {!USE_REAL_API && (
        <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-200">
          {t.simulation}
        </div>
      )}

      {USE_REAL_API && query.isLoading ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">
          {t.loading}
        </div>
      ) : USE_REAL_API && query.error ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-rose-700 ring-1 ring-slate-200">
          {t.loadFailed}
        </div>
      ) : (
        <section
          className="overflow-hidden rounded-lg bg-white ring-1 ring-slate-200"
          aria-label={t.pageTitle}
        >
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
                <Truck size={18} aria-hidden="true" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">ShipStation</div>
                {connected && status?.api_key_hint && (
                  <div className="text-[11px] text-slate-500">{t.keyHint(status.api_key_hint)}</div>
                )}
              </div>
            </div>
            {connected ? (
              <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200 bg-emerald-50">
                <CheckCircle2 size={11} aria-hidden="true" />
                {t.connected}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200">
                {t.notConnected}
              </span>
            )}
          </div>

          {connected && status && (
            <div className="space-y-1 border-b border-slate-100 px-5 py-4 text-xs text-slate-600">
              {status.etsy_store_count !== null && <p>{t.etsyStores(status.etsy_store_count)}</p>}
              {status.verified_at && <p>{t.verifiedAt(formatDateTime(status.verified_at))}</p>}
              {status.last_fill_at && (
                <p>{t.lastFill(formatDateTime(status.last_fill_at), status.last_fill_count ?? 0)}</p>
              )}
              {status.last_error && <p className="text-rose-700">{t.lastError(status.last_error)}</p>}
            </div>
          )}

          {!showForm ? (
            <div className="flex flex-wrap items-center gap-3 px-5 py-4">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                <Lock size={12} aria-hidden="true" />
                {connected ? `••••${status?.api_key_hint ?? ''}` : null}
              </span>
              {!canWrite && (
                <p className="text-xs text-slate-500" data-testid="shipstation-read-only">
                  {t.readOnly}
                </p>
              )}
              {canWrite && (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(t.actions.removeConfirm)) remove.mutate()
                    }}
                    className="rounded-md px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t.actions.remove}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {t.actions.change}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 px-5 py-5">
              <div className="rounded-md bg-slate-50 px-3 py-3 text-xs text-slate-600">
                <p className="font-medium text-slate-700">{t.howTo.title}</p>
                <ol className="mt-1.5 list-decimal space-y-0.5 pl-4">
                  {t.howTo.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-700">{t.fields.apiKey}</span>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    autoComplete="off"
                    className={INPUT_CLASS}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-700">
                    {t.fields.apiSecret}
                  </span>
                  <input
                    type="password"
                    value={apiSecret}
                    onChange={(event) => setApiSecret(event.target.value)}
                    autoComplete="off"
                    className={INPUT_CLASS}
                  />
                </label>
              </div>
              <p className="text-[11px] text-slate-500">{t.fields.secretNote}</p>
              <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
                {connected && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-md border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {t.actions.cancel}
                  </button>
                )}
                <button
                  type="button"
                  disabled={!canSave}
                  onClick={() => save.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <KeyRound size={13} aria-hidden="true" />
                  {save.isPending
                    ? t.actions.saving
                    : connected
                      ? t.actions.rotate
                      : t.actions.save}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
